import { useCallback, useContext } from 'react';
import { BreadcrumbContext } from 'components/providers/BreadcrumbProvider';
import { Breadcrumb } from 'types/breadcrumbs';

type UseBreadcrumbs = {
  additionalBreadcrumbs: Breadcrumb[];
  addBreadcrumbs: (breadcrumbs: Breadcrumb | Breadcrumb[]) => void;
  removeBreadcrumbs: () => void;
  hideBreadcrumbs: () => void;
  showBreadcrumbs: () => void;
};

export const useBreadcrumbs = (): UseBreadcrumbs => {
  const { additionalBreadcrumbs, setAdditionalBreadcrumbs, setShowBreadcrumbs } =
    useContext(BreadcrumbContext);

  const addBreadcrumbs = useCallback(
    (breadcrumbs: Breadcrumb | Breadcrumb[]): void => {
      setAdditionalBreadcrumbs((prev: Breadcrumb[]) => {
        const lastIncoming = Array.isArray(breadcrumbs)
          ? breadcrumbs[breadcrumbs.length - 1]
          : breadcrumbs;
        if (!lastIncoming) return prev;

        const lastPrev = prev[prev.length - 1];
        // early out if breadcrumb is already the last one
        if (lastPrev?.path === lastIncoming.path) {
          return prev;
        }

        return Array.isArray(breadcrumbs) ? [...prev, ...breadcrumbs] : [...prev, breadcrumbs];
      });
    },
    [setAdditionalBreadcrumbs]
  );

  const removeBreadcrumbs = useCallback((): void => {
    setAdditionalBreadcrumbs(() => []);
  }, [setAdditionalBreadcrumbs]);

  const hideBreadcrumbs = useCallback((): void => {
    setShowBreadcrumbs(false);
  }, [setShowBreadcrumbs]);

  const showBreadcrumbs = useCallback((): void => {
    setShowBreadcrumbs(true);
  }, [setShowBreadcrumbs]);

  return {
    additionalBreadcrumbs,
    addBreadcrumbs,
    removeBreadcrumbs,
    hideBreadcrumbs,
    showBreadcrumbs,
  };
};
